import { useState, useEffect, Fragment, useContext } from 'react';
import { ref, onValue } from "firebase/database";
import { db } from '../../utils/firebaseApp.js';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { AuthContext } from "../../utils/auth.js";

export default function Usage({ userId }) {
    const { currentUser } = useContext(AuthContext);
    const adminEmails = ['barzoiu.alexandru@gmail.com', "alexraducu03@gmail.com"];
    const isAdmin = adminEmails.includes(currentUser.email);

    const [usageData, setUsageData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [adminUserId, setAdminUserId] = useState('');
    const [searchedUserId, setSearchedUserId] = useState('');
    const [selectedOrigin, setSelectedOrigin] = useState('all');

    useEffect(() => {
        // Use searchedUserId if admin has searched, otherwise use current userId
        const targetUserId = (isAdmin && searchedUserId) ? searchedUserId : userId;

        if (!targetUserId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const usageRef = ref(db, `billing/${targetUserId}`);

        // Set up real-time listener
        const unsubscribe = onValue(usageRef, (snapshot) => {
            try {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const usageArray = [];

                    console.log(`Reading data from billing/${targetUserId}`);

                    // Process direct entries under billing/{userId}
                    Object.keys(data).forEach((key) => {
                        const value = data[key];

                        // Check if this is a direct entry (has fileId, origin, pagesNo, timestamp fields)
                        // or if it's a nested object (like 'success' or 'error')
                        if (key === 'success' && typeof value === 'object' && value !== null) {
                            // Process entries inside the 'success' path
                            console.log(`Found entries in success path:`, Object.keys(value).length);
                            Object.keys(value).forEach((entryId) => {
                                const entry = value[entryId];
                                usageArray.push({
                                    id: `success_${entryId}`,
                                    fileId: entry?.fileId || 'N/A',
                                    origin: entry?.origin || 'N/A',
                                    pagesNo: entry?.pagesNo !== undefined && entry?.pagesNo !== null ? entry.pagesNo : 'N/A',
                                    timestamp: entry?.timestamp || Date.now(),
                                });
                            });
                        } else if (key === 'error' && typeof value === 'object' && value !== null) {
                            // Process entries inside the 'error' path
                            // Error entries typically only have timestamp, no fileId/origin/pagesNo
                            console.log(`Found entries in error path:`, Object.keys(value).length);
                            Object.keys(value).forEach((entryId) => {
                                const entry = value[entryId];
                                usageArray.push({
                                    id: `error_${entryId}`,
                                    fileId: entry?.fileId || 'N/A',
                                    origin: 'error', // Automatically set origin to "error"
                                    pagesNo: entry?.pagesNo !== undefined && entry?.pagesNo !== null ? entry.pagesNo : 'N/A',
                                    timestamp: entry?.timestamp || Date.now(),
                                });
                            });
                        } else if (typeof value === 'object' && value !== null) {
                            // This is a direct entry or other nested structure
                            // Check if it has the expected structure (with fileId, origin, etc.)
                            if (value.fileId !== undefined || value.origin !== undefined ||
                                value.pagesNo !== undefined || value.timestamp !== undefined) {
                                // This is a direct usage entry
                                usageArray.push({
                                    id: key,
                                    fileId: value?.fileId || 'N/A',
                                    origin: value?.origin || 'N/A',
                                    pagesNo: value?.pagesNo !== undefined && value?.pagesNo !== null ? value.pagesNo : 'N/A',
                                    timestamp: value?.timestamp || Date.now(),
                                });
                            }
                        }
                    });

                    console.log('Total processed usage entries:', usageArray.length);

                    // Sort by timestamp, most recent first
                    usageArray.sort((a, b) => {
                        const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
                        const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
                        return timeB - timeA;
                    });

                    setUsageData(usageArray);
                } else {
                    console.log(`No data found at billing/${targetUserId}`);
                    setUsageData([]);
                }
            } catch (error) {
                console.error('Error processing usage data:', error);
                setUsageData([]);
            } finally {
                setIsLoading(false);
            }
        }, (error) => {
            console.error('Error listening to usage data:', error);
            setUsageData([]);
            setIsLoading(false);
        });

        // Cleanup listener on unmount or when dependencies change
        return () => unsubscribe();
    }, [userId, searchedUserId, isAdmin]);

    const formatDate = (timestamp) => {
        // Handle timestamp in milliseconds format (e.g., 1696304150000)
        const date = new Date(typeof timestamp === 'number' ? timestamp : parseInt(timestamp));

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return date.toLocaleDateString(undefined, options);
    };

    // Extract ALL unique origins from usage data and order them
    const allOrigins = [...new Set(usageData.map(entry => entry.origin))];

    // Define the desired order for origins
    const originOrder = ['all', 'api', 'web', 'syncapi', 'error'];

    // Sort origins: first by defined order, then alphabetically for others
    const sortedOrigins = allOrigins.sort((a, b) => {
        const indexA = originOrder.indexOf(a);
        const indexB = originOrder.indexOf(b);

        // If both are in the order list, sort by their position
        if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
        }

        // If only a is in the order list, it comes first
        if (indexA !== -1) return -1;

        // If only b is in the order list, it comes first
        if (indexB !== -1) return 1;

        // Both are not in the order list, sort alphabetically
        return a.localeCompare(b);
    });

    const uniqueOrigins = ['all', ...sortedOrigins];

    // Origin color mapping - dynamically assigns colors to all origin types
    const getOriginColor = (origin) => {
        const originColorMap = {
            'all': 'bg-gray-100 text-gray-800 hover:bg-gray-200',
            'api': 'bg-blue-100 text-blue-800 hover:bg-blue-200',
            'web': 'bg-green-100 text-green-800 hover:bg-green-200',
            'syncapi': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
            'error': 'bg-red-100 text-red-800 hover:bg-red-200',
            'N/A': 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        };

        if (originColorMap[origin]) {
            return originColorMap[origin];
        }

        // Fallback colors for any other origin types
        const fallbackColors = [
            'bg-purple-100 text-purple-800 hover:bg-purple-200',
            'bg-orange-100 text-orange-800 hover:bg-orange-200',
            'bg-pink-100 text-pink-800 hover:bg-pink-200',
            'bg-teal-100 text-teal-800 hover:bg-teal-200',
            'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
            'bg-amber-100 text-amber-800 hover:bg-amber-200',
            'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
        ];

        // Consistent color assignment based on origin name
        let hash = 0;
        for (let i = 0; i < origin.length; i++) {
            hash = origin.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % fallbackColors.length;
        return fallbackColors[index];
    };

    // Filter usage data by selected origin
    const filteredUsageData = selectedOrigin === 'all'
        ? usageData
        : usageData.filter(entry => entry.origin === selectedOrigin);

    // Pagination logic
    const totalPages = Math.ceil(filteredUsageData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentUsageData = filteredUsageData.slice(startIndex, endIndex);

    // Calculate total pages used
    const totalPagesUsed = usageData.reduce((sum, entry) => {
        const pages = typeof entry.pagesNo === 'number' ? entry.pagesNo : 0;
        return sum + pages;
    }, 0);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const handleAdminSearch = () => {
        if (adminUserId.trim()) {
            setSearchedUserId(adminUserId.trim());
            setCurrentPage(1);
        }
    };

    const handleAdminSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAdminSearch();
        }
    };

    const handleClearSearch = () => {
        setAdminUserId('');
        setSearchedUserId('');
        setCurrentPage(1);
    };

    const handleOriginFilter = (origin) => {
        setSelectedOrigin(origin);
        setCurrentPage(1);
    };

    if (isLoading) {
        return (
            <div className="flex-1 flex items-center justify-center h-32">
                <div role="status">
                    <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-indigo-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (usageData.length === 0) {
        return (
            <>
                {isAdmin && (
                    <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                                Admin
                            </span>
                            <h3 className="text-sm font-semibold text-indigo-900">View usage for any user</h3>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <input
                                type="text"
                                value={adminUserId}
                                onChange={(e) => setAdminUserId(e.target.value)}
                                onKeyPress={handleAdminSearchKeyPress}
                                placeholder="Enter User ID"
                                className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                                onClick={handleAdminSearch}
                                disabled={!adminUserId.trim()}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Search className="h-4 w-4" />
                                Search
                            </button>
                            {searchedUserId && (
                                <button
                                    onClick={handleClearSearch}
                                    className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        {searchedUserId && (
                            <p className="mt-2 text-sm text-indigo-700">
                                Viewing usage for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                            </p>
                        )}
                    </div>
                )}
                <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-gray-900">No Usage Data yet</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            Your usage data will appear here once you start processing documents.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <div className="">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold leading-6 text-gray-900">Usage</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        View all your usage history including file processing and page counts.
                    </p>
                    <p className="mt-1 text-sm font-semibold text-indigo-600">
                        Total Pages Used: {totalPagesUsed.toLocaleString()}
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 flex items-center space-x-2">
                    <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
                        Show:
                    </label>
                    <div className="relative">
                        <select
                            id="itemsPerPage"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="text-sm border border-gray-300 rounded-md pl-2 pr-8 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
                        >
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <span className="text-sm text-gray-700">per page</span>
                </div>
            </div>

            {/* Admin Search */}
            {isAdmin && (
                <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                            Admin
                        </span>
                        <h3 className="text-sm font-semibold text-indigo-900">View usage for any user</h3>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={adminUserId}
                            onChange={(e) => setAdminUserId(e.target.value)}
                            onKeyPress={handleAdminSearchKeyPress}
                            placeholder="Enter User ID"
                            className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                            onClick={handleAdminSearch}
                            disabled={!adminUserId.trim()}
                            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="h-4 w-4" />
                            Search
                        </button>
                        {searchedUserId && (
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {searchedUserId && (
                        <p className="mt-2 text-sm text-indigo-700">
                            Viewing usage for user: <span className="font-mono font-semibold">{searchedUserId}</span>
                        </p>
                    )}
                </div>
            )}

            {/* Origin Filter Badges */}
            {usageData.length > 0 && uniqueOrigins.length > 1 && (
                <div className="mt-6">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-700">Filter by origin:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {uniqueOrigins.map((origin) => {
                            const count = origin === 'all'
                                ? usageData.length
                                : usageData.filter(entry => entry.origin === origin).length;
                            const isSelected = selectedOrigin === origin;

                            return (
                                <button
                                    key={origin}
                                    onClick={() => handleOriginFilter(origin)}
                                    className={`inline-flex items-center gap-x-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${getOriginColor(origin)
                                        } ${isSelected
                                            ? 'ring-2 ring-offset-1 ring-indigo-600'
                                            : ''
                                        }`}
                                >
                                    <span className="capitalize">{origin}</span>
                                    <span className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 text-xs font-semibold rounded-full ${isSelected ? 'bg-white bg-opacity-60' : 'bg-black bg-opacity-10'
                                        }`}>
                                        {count.toLocaleString()}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-4 flow-root">
                {filteredUsageData.length === 0 ? (
                    <div className="mt-4 flex-1 flex flex-col items-center justify-center h-full rounded-lg bg-gray-100 p-8">
                        <div className="text-center">
                            <h2 className="text-xl font-semibold text-gray-900">No usage found for "{selectedOrigin}"</h2>
                            <p className="mt-2 text-sm text-gray-500">
                                Try selecting a different origin or view all usage.
                            </p>
                            <button
                                onClick={() => handleOriginFilter('all')}
                                className="mt-4 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                            >
                                View All Usage
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead>
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                            FILE ID
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            ORIGIN
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            PAGES
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            DATE
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {currentUsageData.map((entry) => (
                                        <tr key={entry.id} className="hover:bg-gray-50">
                                            <td className="py-3 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 break-all">
                                                {entry.fileId}
                                            </td>
                                            <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${getOriginColor(entry.origin)}`}>
                                                    {entry.origin}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                {typeof entry.pagesNo === 'number' ? entry.pagesNo.toLocaleString() : entry.pagesNo}
                                            </td>
                                            <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                                {formatDate(entry.timestamp)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {filteredUsageData.length > 0 && totalPages > 1 && (
                <div className="mt-4 px-4 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                        <div className="flex items-center text-sm text-gray-500">
                            Showing {startIndex + 1} to {Math.min(endIndex, filteredUsageData.length)} of {filteredUsageData.length} results
                            {selectedOrigin !== 'all' && (
                                <span className="ml-1">
                                    (filtered from {usageData.length} total)
                                </span>
                            )}
                        </div>

                        <div className="flex items-center space-x-1">
                            {/* First Page */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="First page"
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <ChevronLeft className="h-4 w-4 -ml-1" />
                            </button>

                            {/* Previous Page */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Previous
                            </button>

                            {/* Page Numbers */}
                            <div className="flex items-center space-x-1">
                                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                    let pageNumber;
                                    if (totalPages <= 5) {
                                        pageNumber = i + 1;
                                    } else if (currentPage <= 3) {
                                        pageNumber = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                        pageNumber = totalPages - 4 + i;
                                    } else {
                                        pageNumber = currentPage - 2 + i;
                                    }

                                    return (
                                        <button
                                            key={pageNumber}
                                            onClick={() => handlePageChange(pageNumber)}
                                            className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNumber
                                                ? 'bg-indigo-600 text-white'
                                                : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Next Page */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </button>

                            {/* Last Page */}
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Last page"
                            >
                                <ChevronRight className="h-4 w-4" />
                                <ChevronRight className="h-4 w-4 -ml-1" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

